import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import type {
  ColumnSizingState,
  Table,
  VisibilityState,
} from "@tanstack/react-table";

interface UseColumnResizeOptions<TData> {
  enabled: boolean;
  table: Table<TData>;
  columnVisibility: VisibilityState;
  hasSelectionColumn: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  columnSizing: ColumnSizingState;
  setColumnSizing: (
    updater: ColumnSizingState | ((old: ColumnSizingState) => ColumnSizingState)
  ) => void;
  initialUserResizedIds?: string[];
  onUserResize?: (
    sizing: ColumnSizingState,
    userResizedIds: Set<string>
  ) => void;
}

export const useColumnResize = <TData>({
  enabled,
  table,
  columnVisibility,
  hasSelectionColumn,
  scrollContainerRef,
  columnSizing,
  setColumnSizing,
  initialUserResizedIds,
  onUserResize,
}: UseColumnResizeOptions<TData>) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const userResizedColumnsRef = useRef<Set<string>>(
    new Set(initialUserResizedIds ?? [])
  );
  const columns = table.options.columns;

  const clearUserResized = useCallback(
    (columnId: string) => {
      userResizedColumnsRef.current.delete(columnId);
      onUserResize?.(
        table.getState().columnSizing,
        userResizedColumnsRef.current
      );
    },
    [onUserResize, table]
  );

  const trackUserResize = useCallback(
    (
      updaterOrValue:
        | ColumnSizingState
        | ((old: ColumnSizingState) => ColumnSizingState)
    ) => {
      setColumnSizing(prev => {
        const next =
          typeof updaterOrValue === "function"
            ? updaterOrValue(prev)
            : updaterOrValue;

        for (const id of Object.keys(next)) {
          if (next[id] !== prev[id]) {
            userResizedColumnsRef.current.add(id);
          }
        }

        onUserResize?.(next, userResizedColumnsRef.current);

        return next;
      });
    },
    [setColumnSizing, onUserResize]
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !enabled) return;

    let rafId: number;
    const observer = new ResizeObserver(entries => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setContainerWidth(Math.round(entries[0].contentRect.width));
      });
    });
    observer.observe(container);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [enabled, scrollContainerRef]);

  useLayoutEffect(() => {
    if (!enabled) return;

    const container = scrollContainerRef.current;
    const measuredWidth = Math.max(
      container ? Math.round(container.clientWidth) : 0,
      containerWidth
    );

    if (measuredWidth === 0) return;

    const leafColumns = table
      .getAllLeafColumns()
      .filter(col => col.id !== "_selection");

    let reservedWidth = 0;
    if (hasSelectionColumn) {
      const firstTh = container?.querySelector("thead th");
      if (firstTh) {
        reservedWidth = firstTh.getBoundingClientRect().width;
      }
    }

    const availableWidth = measuredWidth - reservedWidth;

    let userResizedTotal = 0;
    let autoDefaultTotal = 0;

    const visibleIds = new Set(leafColumns.map(col => col.id));
    for (const id of userResizedColumnsRef.current) {
      if (!visibleIds.has(id)) userResizedColumnsRef.current.delete(id);
    }

    leafColumns.forEach(col => {
      if (userResizedColumnsRef.current.has(col.id)) {
        userResizedTotal += columnSizing[col.id] ?? col.columnDef.size ?? 150;
      } else {
        autoDefaultTotal += col.columnDef.size ?? 150;
      }
    });

    if (autoDefaultTotal === 0) return;

    const autoAvailable = availableWidth - userResizedTotal;
    const scale = autoAvailable > 0 ? autoAvailable / autoDefaultTotal : 0;
    const newSizing: ColumnSizingState = {};
    let changed = false;

    leafColumns.forEach(col => {
      if (userResizedColumnsRef.current.has(col.id)) {
        newSizing[col.id] = columnSizing[col.id] ?? col.columnDef.size ?? 150;
      } else {
        const defaultSize = col.columnDef.size ?? 150;
        const newSize =
          scale > 0 ? Math.floor(defaultSize * scale) : defaultSize;

        if (newSize !== (columnSizing[col.id] ?? -1)) changed = true;
        newSizing[col.id] = newSize;
      }
    });

    if (changed) setColumnSizing(prev => ({ ...prev, ...newSizing }));
  }, [containerWidth, enabled, columns, columnVisibility, hasSelectionColumn]);

  const lastDataColumnId = enabled
    ? (() => {
        const dataCols = table
          .getVisibleLeafColumns()
          .filter(col => col.id !== "_selection");
        const lastNonPinned = dataCols.filter(col => !col.getIsPinned()).at(-1);

        return (lastNonPinned ?? dataCols.at(-1))?.id;
      })()
    : undefined;

  return { trackUserResize, lastDataColumnId, clearUserResized };
};
